import React, { useEffect, useMemo, useState, Dispatch, SetStateAction } from 'react'
import { Table, TableHeader, TextCell, ActionCell, ActionCellProps } from '@amzn/aapn-react-components'
import { Trans, useTranslation } from 'react-i18next'
import {
  useGetAdvertiserInvitesQuery,
  PartnerAdvertiserInviteEdge,
  PartnerAdvertiserInvite,
  useDeletePartnerAdvertiserInviteMutation,
  DeletePartnerAdvertiserInviteMutation,
  DeletePartnerAdvertiserInviteMutationVariables,
} from 'generated/graphql'
import { toNullable } from 'fp-ts/es6/Option'
import { AdvertisersNoDataBody } from './advertisers_no_data_body'
import { AdvertisersNoResultsBody } from './advertisers_no_results_body'
import { PrevCursor, addNewCursor } from './advertisers_helper'
import { AdvertisersPagination } from './advertisers_pagination'
import { AdvertiserInvitesResend } from './advertiser_invites_resend'
import { AdvertisersDeleteModal, AdvertiserInvitesDeleteModalProps, AdvertisersDeleteHeader } from './advertisers_delete_modal'
import * as i18nKeyList from './link_advertisers_v2_i18n_mappings'
import { capitalize } from 'util/string_helper'
import { ExecutionResult } from 'graphql'
import { PARTNER_ID_HEADER_KEY } from 'util/app_initialization/apollo_client/stagedive_apollo_client_provider'
import Skeleton from 'react-loading-skeleton'
import { MutationFunctionOptions } from '@apollo/client'
import { StyledTableContainer, TableContainer } from './link_advertisers_v2.styles'
import { TableLoadErrorBody } from '../../../../../shared/components/errors/error_table_load'
import { useLoginContext } from 'stagedive/LoginContext'
import { useLanguageContext } from 'stagedive/LanguageContext'
export const PAGE_SIZE = 50

interface LinkAdvertisersV2Props {
  setSuccessAlert: Dispatch<SetStateAction<boolean>>
}
export const LinkAdvertisersV2: React.FunctionComponent<LinkAdvertisersV2Props> = (props: LinkAdvertisersV2Props): JSX.Element => {
  const [pageNum, setPageNum] = useState<number>(0)
  const [cursor, setCursor] = useState<string | null>(null)
  const [prevCursors, setPrevCursors] = useState<PrevCursor[]>([])
  const { t } = useTranslation()
  const { loginContext } = useLoginContext()
  const partnerAccountId = toNullable(loginContext.partnerProfileId) ?? ''
  const [advertiserInvites, setAdvertiserInvites] = useState<PartnerAdvertiserInvite[]>([])
  const { language } = useLanguageContext()

  const {
    loading: getAdvertiserInviteLoading,
    error,
    data: response,
  } = useGetAdvertiserInvitesQuery({
    variables: {
      first: PAGE_SIZE,
      after: cursor,
    },
    context: {
      headers: {
        [PARTNER_ID_HEADER_KEY]: partnerAccountId,
      },
    },
  })

  useEffect(() => {
    if (!getAdvertiserInviteLoading) {
      if (response?.partner?.advertiserInvites?.edges?.length) {
        addNewCursor(prevCursors, pageNum, setPrevCursors, response?.partner?.advertiserInvites?.pageInfo?.endCursor)
      } else {
        setPageNum(0)
        setCursor(null)
      }
    }
  }, [response, pageNum, prevCursors, getAdvertiserInviteLoading])

  useEffect(() => {
    const nullableAdvertiserInviteEdges = response?.partner?.advertiserInvites?.edges as PartnerAdvertiserInviteEdge[]
    if (nullableAdvertiserInviteEdges) {
      const invites = nullableAdvertiserInviteEdges.map((inviteEdge: PartnerAdvertiserInviteEdge): PartnerAdvertiserInvite => {
        return {
          __typename: 'PartnerAdvertiserInvite',
          invitationToken: inviteEdge.node.invitationToken,
          inviteeEmail: inviteEdge.node.inviteeEmail,
          creationDate: inviteEdge.node.creationDate,
          status: inviteEdge.node.status,
          partner: {
            __typename: 'Partner',
            id: inviteEdge.node.partner?.id,
          },
          invitedBy: {
            __typename: 'User',
            id: inviteEdge.node.invitedBy?.id,
          },
        }
      })
      setAdvertiserInvites(invites)
    }
  }, [response])

  const columns = useMemo(
    () => [
      {
        id: t(i18nKeyList.TABLE_HEADER_ACCOUNT),
        flexGrow: 2,
        Header: TableHeader,
        Cell: TextCell,
        accessor: (advertiserInvite: PartnerAdvertiserInvite): string => {
          return advertiserInvite.inviteeEmail
        },
        show: false,
      },
      {
        id: t(i18nKeyList.TABLE_HEADER_REQUESTED),
        flexGrow: 0.5,
        Header: TableHeader,
        Cell: TextCell,
        accessor: (advertiserInvite: PartnerAdvertiserInvite): string => {
          const creationDate = advertiserInvite.creationDate
          return new Date(creationDate).toLocaleDateString(language, {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
          })
        },
      },
      {
        id: t(i18nKeyList.TABLE_HEADER_STATUS),
        flexGrow: 0.5,
        Header: TableHeader,
        Cell: ActionCell,
        accessor: (advertiserInvite: PartnerAdvertiserInvite): ActionCellProps => {
          const advertiserInviteStatus = capitalize(advertiserInvite.status)
          return {
            mainLabel: advertiserInviteStatus,
            ActionComponent: (
              <AdvertiserInvitesResend
                key={advertiserInvite.invitationToken}
                inviteToken={advertiserInvite.invitationToken}
                partnerId={partnerAccountId}
                onSetData={() => undefined}
              />
            ),
          }
        },
      },
      {
        id: t(i18nKeyList.TABLE_HEADER_ACTION),
        Header: TableHeader,
        flexGrow: 0.1,
        Cell: AdvertisersDeleteModal,
        accessor: (advertiserInvite: PartnerAdvertiserInvite): AdvertiserInvitesDeleteModalProps => {
          const deleteMutationHook = useDeletePartnerAdvertiserInviteMutation
          return {
            inviteeEmail: advertiserInvite.inviteeEmail,
            inviteToken: advertiserInvite.invitationToken,
            onSetData: (
              deleteAdvertiserInvite: (
                options?: MutationFunctionOptions<DeletePartnerAdvertiserInviteMutation, DeletePartnerAdvertiserInviteMutationVariables>,
              ) => Promise<ExecutionResult<DeletePartnerAdvertiserInviteMutation>>,
            ): Promise<void> => {
              props.setSuccessAlert(false)
              return deleteAdvertiserInvite({
                variables: {
                  inviteId: advertiserInvite.invitationToken,
                },
                context: {
                  headers: {
                    [PARTNER_ID_HEADER_KEY]: partnerAccountId,
                  },
                },
              }).then((response: ExecutionResult<DeletePartnerAdvertiserInviteMutation>) => {
                const deletedInvite = response?.data?.deletePartnerAdvertiserInvite
                if (deletedInvite?.__typename === 'PartnerAdvertiserInvite') {
                  setAdvertiserInvites((prev) =>
                    prev.filter((currentInvite: PartnerAdvertiserInvite) => currentInvite.invitationToken !== deletedInvite.invitationToken),
                  )
                  props.setSuccessAlert(true)
                }
              })
            },
            deleteMutationHook,
            callToActionLabel: t(i18nKeyList.ADVERTISER_INVITES_REMOVE_CTA),
            cancelLabel: t(i18nKeyList.ADVERTISER_INVITES_REMOVE_CANCEL_LABEL),
            loadingLabel: t(i18nKeyList.ADVERTISER_INVITES_LOADING_LABEL),
            sendLabel: t(i18nKeyList.ADVERTISER_INVITES_REMOVE_SEND_LABEL),
            deleteLabel: (
              <Trans
                i18nKey={i18nKeyList.ADVERTISER_INVITES_REMOVE_MESSAGE}
                values={{ inviteeEmail: advertiserInvite.inviteeEmail }}
                components={[<strong key={'key'} />]}
              />
            ),
            header: <AdvertisersDeleteHeader headerLabel={t(i18nKeyList.ADVERTISER_INVITES_REMOVE_HEADER)} />,
            removeErrorMessage: t(i18nKeyList.ADVERTISER_INVITES_REMOVE_ERROR),
          }
        },
      },
    ],
    [t, partnerAccountId],
  )

  const tableColumns = useMemo(() => {
    return getAdvertiserInviteLoading
      ? columns.map((column) => ({
          ...column,
          Cell: <Skeleton />,
        }))
      : columns
  }, [columns, getAdvertiserInviteLoading])

  const tableProps = {
    columns: tableColumns,
    data: getAdvertiserInviteLoading ? Array(PAGE_SIZE).fill({}) : advertiserInvites,
    hasRefinementsSet: false,
    hideBorder: false,
    hasLoadError: !!error || (!response && !getAdvertiserInviteLoading),
    // eslint-disable-next-line react/display-name
    NoDataBody: () => (
      <AdvertisersNoDataBody
        noDataBodyLabel={t(i18nKeyList.NO_ADVERTISER_INVITES_DATA_BODY)}
        noDataHeaderLabel={t(i18nKeyList.NO_ADVERTISER_INVITES_DATA_HEADER)}
      />
    ),
    // eslint-disable-next-line react/display-name
    NoResultsBody: () => (
      <AdvertisersNoResultsBody
        noResultsBodyLabel={t(i18nKeyList.NO_ADVERTISER_INVITES_RESULTS_BODY)}
        noResultsHeaderLabel={t(i18nKeyList.NO_ADVERTISER_INVITES_RESULTS_HEADER)}
      />
    ),
    // eslint-disable-next-line react/display-name
    ErrorBody: () => (
      <TableLoadErrorBody
        errorHeaderLabel={t(i18nKeyList.INVITE_ADVERTISER_ERROR_HEADER)}
        errorBodyLabel={t(i18nKeyList.INVITE_ADVERTISER_ERROR_MESSAGE)}
        errorCallToAction={t(i18nKeyList.INVITE_ADVERTISER_ERROR_CALL_TO_ACTION)}
      />
    ),
  }

  const advertisersPaginationProps = {
    loading: getAdvertiserInviteLoading,
    cursor: response?.partner?.advertiserInvites?.pageInfo?.endCursor,
    setCursor,
    pageNum,
    setPageNum,
    prevCursors,
    hasNextPage: response?.partner?.advertiserInvites?.pageInfo?.hasNextPage,
  }

  return (
    <TableContainer>
      <StyledTableContainer>
        <Table {...tableProps} />
      </StyledTableContainer>
      <AdvertisersPagination {...advertisersPaginationProps} />
    </TableContainer>
  )
}
